import { copyWallSplitLineages } from './engine/wallSplitLineage.js';
import { reviewRoomAreaRemoval, applyRoomAreaRemovalReview } from './engine/roomAreaRemoval.js';
import { validateWallEditSite } from './engine/wallThickness.js';
import { reconcileWallLocksForHistory } from './engine/wallLockHistory.js';
import { canonicalHistoryDocument, HISTORY_LIMITS, historyError, validateHistoryState } from './engine/editorHistory.js';
import { assertWallLengthLockTransition, preserveWallLengthLocksForRestore, copyWallLengthLocks } from './engine/wallLengthLocks.js';
import { useSyncExternalStore } from 'react';
import { blankDoc, uid, clamp } from './engine/constants.js';
import { audit, buildGrid, evaluate } from './engine/coverage.js';
import { snapWallCamera, validWallPose } from './engine/mounting.js';
import { cameraMountKind, isCeilingCamera, isCeilingKind, isPoleKind, isPoleCamera, placementMountKind, mountingProfile } from './engine/cameraMounts.js';
import { ceilingCameraPose, ceilingPlacement, syncCeilingCameras } from './engine/ceilingMounting.js';
import { poleCameraPose, snapPoleCamera, syncPoleCameras } from './engine/poleMounting.js';
import { chooseProfile } from './engine/standards.js';
import { defaultStair } from './engine/stairs.js';
import { startAnalysis } from './engine/analysisClient.js';
import { beginActivity } from './activity.js';
import { lightingFingerprint } from './engine/lightingSolver.js';
import { timePreviewDocument } from './engine/lightingTime.js';
import { activateSiteLevel, buildingID, buildingFloors, floorSnapshot, registerBuildings, separateBuilding, siteBuildings } from './engine/siteBuildings.js';
import { formatLength } from './engine/units.js';
import { SITE_GROUND, groundFloor, groundLevels, groundOwnerAt, groundSelection, isGroundLevel, siteGroundPlan } from './engine/siteGround.js';

/**
 * The plan is a mutable document with a revision counter, not immutable state.
 *
 * That is a deliberate choice: the canvas editor, the coverage engine and the
 * solver are all imperative and touch the document thousands of times per
 * evaluation. Cloning it on every wall drag to satisfy React would cost more
 * than the physics does. Instead the document is mutated in place, `touch()`
 * bumps a counter, and components re-read through useSyncExternalStore — React
 * owns the shell, the store owns the geometry.
 */
export class PlanStore {
  constructor(doc = blankDoc()) {
    this.doc = { ...blankDoc(), ...doc };
    this.catalogue = new Map();
    this.rev = 0;
    this.cov = null;
    this.dirty = false;
    this.savedRev = 0;
    this.sel = null;
    this.probe = null;
    this.snapCameras = true;
    this.tool = 'select';
    this.live = true;
    this.busy = false;
    this.coverageError = '';
    this.editError = '';
    this.roomAreaRemovalRequest = null;
    this._coverageJob = null;
    this.lightingResult = null;
    this.lightingError = '';
    this.lightingEnabled = false;
    this._lightingJob = null;
    this._lightingTimer = null;
    this.lightingTime = null;
    this.lightingTimeResult = null;
    this.lightingTimeError = '';
    this._lightingTimeJob = null;
    this._lightingTimeTimer = null;
    this.listeners = new Set();
    this.undoStack = [];
    this.redoStack = [];
    this._evalTimer = null;
    this.subscribe = this.subscribe.bind(this);
    this.getRev = this.getRev.bind(this);
  }

  subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  getRev() { return this.rev; }

  emit() {
    this.rev++;
    for (const l of this.listeners) l();
  }

  /** Something about the plan changed: re-evaluate (debounced) and repaint. */
  touch({ dirty = true, reevaluate = true } = {}) {
    if (dirty) this.roomAreaRemovalRequest = null;
    if (dirty) this.endLightingTime(false);
    if (dirty) { syncCeilingCameras(this.doc); syncPoleCameras(this.doc); }
    if (dirty && this._edit && this._snap() !== this._edit.before) this.snapshot();
    // Counts edits to the plan, so autosave can tell them from selection and view changes.
    if (dirty) { this.dirty = true; this.changes = (this.changes || 0) + 1; }
    if (dirty && (this.lightingEnabled || this.lightingResult) && this.lightingResult?.fingerprint !== lightingFingerprint(this.doc)) {
      this.cancelLighting(); this.lightingResult = null;
      if (this.lightingEnabled && this.doc.lighting.scenarios.length) this._lightingTimer = setTimeout(() => this.evaluateLighting(), 450);
    }
    if (reevaluate) {
      this.cancelEvaluation();
      this.cov = null;
      if (this.live) {
        clearTimeout(this._evalTimer);
        this._evalTimer = setTimeout(() => this.evaluate(), 90);
      }
    }
    this.emit();
  }

  evaluate() {
    clearTimeout(this._evalTimer);
    // Engine-only Node consumers remain synchronous. Browsers always calculate
    // in a worker, keeping editing, activity animation and cancellation live.
    if (typeof Worker === 'undefined') {
      const G = buildGrid(this.doc), visibility = { previous: this._visibility };
      this.cov = G ? evaluate(this.doc, G, { visibility }) : null; this._visibility = visibility.next; this.emit(); return;
    }
    if (this._coverageJob) return;
    this.busy = true; this.coverageError = '';
    const job = { activity: beginActivity('Calculating floor coverage…'), task: null };
    this._coverageJob = job; this.emit();
    const finish = () => { job.activity.finish(); if (this._coverageJob === job) { this._coverageJob = null; this.busy = false; this.emit(); } };
    try {
      // The worker gets a copy of the last run's sight-line results and returns this run's.
      // A cancelled run leaves the copy held here untouched.
      job.task = startAnalysis('coverage', { doc: this.doc, visibility: this._visibility }, (p, msg) => job.activity.update({ progress: p, message: msg }),
        message => { if (this._coverageJob === job && message.visibility instanceof Map) this._visibility = message.visibility; });
      job.task.result.then(result => { if (this._coverageJob === job) this.cov = result; }, error => { if (this._coverageJob === job && error.name !== 'AbortError') { this.cov = null; this.coverageError = error.message; } }).finally(finish);
    } catch (error) { this.coverageError = error.message; finish(); }
  }

  cancelEvaluation() {
    clearTimeout(this._evalTimer);
    const job = this._coverageJob; this._coverageJob = null; this.busy = false;
    job?.task?.cancel(); job?.activity.finish();
  }

  enableLighting() {
    this.lightingEnabled = true;
    if (this.doc.lighting.scenarios.length && !this._lightingJob && this.lightingResult?.fingerprint !== lightingFingerprint(this.doc)) this.evaluateLighting();
  }
  evaluateLighting() {
    this.cancelLighting(); this.lightingEnabled = true; this.lightingError = '';
    if (!this.doc.lighting.scenarios.length || typeof Worker === 'undefined') { this.emit(); return; }
    const fingerprint = lightingFingerprint(this.doc);
    const job = { activity: beginActivity('Assessing lighting…', { cancel: () => this.cancelLighting(true) }), task: null };
    this._lightingJob = job; this.emit();
    const finish = () => { job.activity.finish(); if (this._lightingJob === job) { this._lightingJob = null; this.emit(); } };
    try {
      job.task = startAnalysis('lighting', { doc: this.doc }, (p, msg) => job.activity.update({ progress: p, message: msg }));
      job.task.result.then(result => {
        if (this._lightingJob === job && fingerprint === lightingFingerprint(this.doc)) this.lightingResult = result;
      }, error => { if (this._lightingJob === job && error.name !== 'AbortError') this.lightingError = error.message; }).finally(finish);
    } catch (error) { this.lightingError = error.message; finish(); }
  }
  cancelLighting(manual = false) {
    clearTimeout(this._lightingTimer);
    const job = this._lightingJob; this._lightingJob = null;
    job?.task?.cancel(); job?.activity.finish();
    if (manual) { this.lightingEnabled = false; this.lightingError = 'Assessment canceled. Run the check when ready.'; this.emit(); }
  }

  // Exploration is view state. Saves, undo, reports and the planner continue
  // reading doc and lightingResult until the user explicitly saves a condition.
  get lightingViewDoc() { return this.lightingTime ? timePreviewDocument(this.doc, this.lightingTime) : this.doc; }
  exploreLightingTime(timestamp = this.doc.lighting.scenarios.find(s => s.id === this.doc.lighting.activeId)?.timestamp || new Date().toISOString()) {
    if (!this.doc.lighting.scenarios.some(s => s.id === this.doc.lighting.activeId) || !Number.isFinite(Date.parse(timestamp))) return;
    this.endLightingTime(false);
    this.lightingTime = timestamp;
    this._lightingTimeTimer = setTimeout(() => this.evaluateLightingTime(), 450);
    this.emit();
  }
  evaluateLightingTime() {
    clearTimeout(this._lightingTimeTimer);
    if (!this.lightingTime || typeof Worker === 'undefined') return;
    const doc = this.lightingViewDoc, fingerprint = lightingFingerprint(doc);
    const job = { activity: beginActivity('Checking explored time…', { cancel: () => this.endLightingTime() }), task: null };
    this._lightingTimeJob = job; this.emit();
    const finish = () => { job.activity.finish(); if (this._lightingTimeJob === job) { this._lightingTimeJob = null; this.emit(); } };
    try {
      job.task = startAnalysis('lighting', { doc }, (p, msg) => job.activity.update({ progress: p, message: msg }));
      job.task.result.then(result => {
        if (this._lightingTimeJob === job && this.lightingTime && fingerprint === lightingFingerprint(this.lightingViewDoc)) this.lightingTimeResult = result;
      }, error => { if (this._lightingTimeJob === job && error.name !== 'AbortError') this.lightingTimeError = error.message; }).finally(finish);
    } catch (error) { this.lightingTimeError = error.message; finish(); }
  }
  endLightingTime(emit = true) {
    clearTimeout(this._lightingTimeTimer);
    const job = this._lightingTimeJob; this._lightingTimeJob = null;
    job?.task?.cancel(); job?.activity.finish();
    this.lightingTime = null; this.lightingTimeResult = null; this.lightingTimeError = '';
    if (emit) this.emit();
  }
  saveExploredCondition() {
    if (!this.lightingTime || this.doc.lighting.scenarios.length >= 12) return;
    const scenario = structuredClone(this.lightingViewDoc.lighting.scenarios[0]);
    scenario.id = uid('lightcase'); scenario.name = scenario.name.replace(' · exploration', '').slice(0, 160);
    scenario.notes = `Time exploration saved with unchanged daylight level assumptions. ${scenario.notes}`.slice(0, 2000);
    this.snapshot(); this.doc.lighting.scenarios.push(scenario); this.doc.lighting.activeId = scenario.id;
    this.touch({ reevaluate: false });
  }

  /* ---- selection & tool ---- */
  get groundViewDoc() {
    if (this._groundView?.rev !== this.rev || this._groundView?.source !== this.doc) {
      this._groundView = { rev: this.rev, source: this.doc, plan: siteGroundPlan(this.doc) };
    }
    return this._groundView.plan;
  }
  activateGroundSelection(sel) {
    const resolved = groundSelection(this.groundViewDoc, sel);
    if (!resolved?.floorId) return resolved;
    if (!isGroundLevel(this.doc) || !groundLevels(this.doc).some(f => f.id === resolved.floorId)) return null;
    if (resolved.floorId !== this.doc.activeFloorId) this.switchFloor(resolved.floorId, { viewOnly: true });
    const { floorId, ...selection } = resolved;
    return selection;
  }
  activateGroundAt(point) {
    const id = groundOwnerAt(this.doc, point);
    if (id && id !== this.doc.activeFloorId) this.switchFloor(id, { viewOnly: true });
  }
  select(sel) { this.sel = this.activateGroundSelection(sel); this.emit(); return this.sel; }
  setTool(tool) { this.tool = tool; this.emit(); }
  selected() {
    const s = this.sel, d = this.doc;
    if (!s) return null;
    if (s.type === 'light') return this.doc.lights.find(l => l.id === s.id) || null;
    if (s.type === 'stair') return d.stairs.find(t => t.id === s.id) || null;
    if (s.type === 'cam') return d.cams.find(c => c.id === s.id) || null;
    if (s.type === 'cable') return d.cables.find(c => c.id === s.id) || null;
    if (s.type === 'target') return d.targets.find(t => t.id === s.id) || null;
    if (s.type === 'equipment') return d.equipment.find(e => e.id === s.id) || null;
    if (s.type === 'wall') return d.walls.find(w => w.id === s.id) || null;
    if (s.type === 'area') return d.areas.find(a => a.id === s.id) || null;
    if (s.type === 'zone') return d.zones.find(z => z.id === s.id) || null;
    if (s.type === 'task') return d.tasks.find(t => t.id === s.id) || null;
    if (s.type === 'obstacle') return d.obstacles.find(t => t.id === s.id) || null;
    if (s.type === 'opening') return d.openings.find(t => t.id === s.id) || null;
    if (s.type === 'bg') return d.bg;
    return null;
  }

  /* A field edit is one history entry, even when it emits on every keystroke.
     The cache and transient selection are not part of document history. */
  beginEdit(key) {
    if (this._edit?.key === key) return;
    this.endEdit();
    this._edit = { key, before: this._snap(), captured: false, redo: [...this.redoStack] };
  }
  endEdit() {
    const edit = this._edit;
    this._edit = null;
    if (edit?.captured && this._snap() === edit.before && this.undoStack.at(-1) === edit.before) {
      this.undoStack.pop(); this.redoStack = edit.redo;
    }
  }
  _snap() {
    const d = this.doc;
    return JSON.stringify(this.toDocument());
  }
  snapshot() {
    if (this._edit?.captured) return;
    this.undoStack.push(this._edit?.before ?? this._snap());
    if (this._edit) this._edit.captured = true;
    if (this.undoStack.length > 60) this.undoStack.shift();
    this.redoStack.length = 0;
  }
  _apply(o) {
    const floorId = this.doc.activeFloorId;
    // A snapshot from before the first reviewed floor-elevation entry has no
    // optional original-input record. Object.assign alone leaves it behind.
    if (!Object.hasOwn(o, 'floorElevationInput')) delete this.doc.floorElevationInput;
    Object.assign(this.doc, o);
    if (floorId !== this.doc.activeFloorId || !this.selected()) this.sel = null;
    this.touch();
  }
  reportEditError(message) { if(this.editError!==message){this.editError=message;this.emit();} return false; }
  _history(from,to,reason) {
    this.endEdit();
    if(!from.length)return;
    try {
      const next=reconcileWallLocksForHistory(this.toDocument(),JSON.parse(from.at(-1)),reason);
      to.push(this._snap());from.pop();this.editError='';this._apply(next);
      return true;
    } catch(error){return this.reportEditError(error.message);}
  }
  undo() { return this._history(this.undoStack,this.redoStack,'Undo'); }
  redo() { return this._history(this.redoStack,this.undoStack,'Redo'); }
  get canUndo() { return this.undoStack.length > 0; }
  get canRedo() { return this.redoStack.length > 0; }

  /** Internal adapter only: callers must verify account, checkpoint ownership and
   * saved-head attachment separately. This does not read or write durable state. */
  exportHistoryState({ candidate } = {}) {
    const present = this.toDocument();
    const undo = [...this.undoStack];
    let redo = [...this.redoStack];
    // Model endEdit without ending a live field session or consuming its redo.
    if (this._edit?.captured && this._snap() === this._edit.before && undo.at(-1) === this._edit.before) {
      undo.pop(); redo = [...this._edit.redo];
    }
    if (undo.length + redo.length > HISTORY_LIMITS.entries) throw historyError('History state exceeds its entry bounds.');
    // Bound serialized snapshots before parsing them; structured validation then
    // bounds the complete present + stacks, not each document independently.
    let bytes = 0;
    const decode = value => {
      if (typeof value !== 'string') throw historyError('History snapshot is unreadable.');
      bytes += new TextEncoder().encode(value).length;
      if (bytes > HISTORY_LIMITS.jsonBytes) throw historyError('History exceeds the 8 MiB document limit.', 'history-limit');
      try { return JSON.parse(value); }
      catch { throw historyError('History snapshot is unreadable.'); }
    };
    const state = validateHistoryState({ present, undo: undo.map(decode), redo: redo.map(decode) });
    if (candidate === undefined) return state;
    // A proposed drawing import is its own edit, including after a grouped edit.
    // At capacity use the same oldest-entry eviction as an ordinary snapshot.
    return validateHistoryState({
      present: this.toDocument(candidate),
      undo: [...state.undo, state.present].slice(-HISTORY_LIMITS.entries), redo: [],
    });
  }

  restoreHistoryState(state, { checkpointVerified = false } = {}) {
    if (checkpointVerified !== true) throw historyError('Verify the saved history checkpoint before restoring it.');
    if (this.dirty || this._edit || this.undoStack.length || this.redoStack.length ||
        this.busy || this._coverageJob || this._lightingJob || this._lightingTimeJob || this.lightingTime) {
      throw historyError('History can only be restored into a clean idle editor without new edits.');
    }
    const detached = validateHistoryState(state);
    if (canonicalHistoryDocument(this.toDocument()) !== canonicalHistoryDocument(detached.present)) {
      throw historyError('The current Site no longer matches the saved history checkpoint.', 'history-stale');
    }
    // Complete validation and serialization before either stack changes. Keep the
    // current document/view/selection and saved revision; ordinary Undo/Redo still
    // calls reconcileWallLocksForHistory before applying any historical geometry.
    const undo = detached.undo.map(value => JSON.stringify(value));
    const redo = detached.redo.map(value => JSON.stringify(value));
    this.undoStack = undo; this.redoStack = redo;
    // rev is React's subscription counter, not the server/saved revision.
    // Notify availability once without dirtying or evaluating the document.
    if (undo.length || redo.length) this.emit();
    return true;
  }

  /* ---- wholesale replacement (load, restore, new) ---- */
  setDoc(doc, { markClean = true, preserveLocks = false } = {}) {
    this.roomAreaRemovalRequest = null;
    if(preserveLocks){
      // A recovered draft may contain a valid explicit unlock. Preserve that
      // transition; older backups still inherit current constraints/history.
      try { assertWallLengthLockTransition(this.toDocument(),doc); }
      catch { doc=preserveWallLengthLocksForRestore(this.toDocument(),doc); }
    }
    this.editError='';
    this.endLightingTime(false);
    this.cancelEvaluation();
    this.cancelLighting(); this.lightingResult = null; this.lightingError = ''; this.lightingEnabled = false;
    this._edit = null;
    const used = new Set(doc.cams.map(c => c.modelId));
    for (const m of doc.models) if (!this.projectId && !this.catalogue.has(m.id)) this.catalogue.set(m.id, { ...m });
    this.doc = { ...blankDoc(), ...doc, models: doc.models.filter(m => used.has(m.id)) };
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.sel = null;
    this.probe = null;
    this.cov = null;
    this.dirty = !markClean;
    this.evaluate();
  }

  markSaved() { this.dirty = false; this.emit(); }

  /** The library is a cache, not part of the plan's saved specifications. */
  rememberModels(models) {
    for (const m of models) this.catalogue.set(m.id, { ...m });
    this.emit();
  }
  setSiteModels(models) {
    this.catalogue.clear();
    for (const m of models) this.catalogue.set(m.id, { ...m });
    if (!this.catalogue.has(this.newModelId)) this.newModelId = models[0]?.id || '';
    this.emit();
  }
  get availableModels() {
    if (this.projectId) return [...this.catalogue.values()];
    return [...new Map([...this.catalogue.values(), ...this.doc.models].map(m => [m.id, m])).values()];
  }
  model(id) { return this.doc.models.find(m => m.id === id) || this.catalogue.get(id); }
  withProjectQuote(model) {
    const q = this.doc.planning?.quotes?.find(q => q.modelId === model.id);
    return q ? { ...model, cost: q.cost, currency: q.currency, supplier: q.supplier || '', priceDate: q.priceDate || '' } : model;
  }
  useModel(id) {
    const m = this.model(id);
    if (m && !this.doc.models.some(q => q.id === id)) this.doc.models.push({ ...this.withProjectQuote(m) });
    return m;
  }
  toDocument(plan = this.doc) {
    const used = new Set(plan.cams.map(c => c.modelId));
    return { ...plan, models: plan.models.filter(m => used.has(m.id)) };
  }

  /* ---- mutations the UI reaches for ---- */
  addCamera(x, y, modelId, opts = {}) {
    const d = this.doc;
    if (this.projectId && modelId && !this.catalogue.has(modelId)) return null;
    const m = this.model(modelId) || this.availableModels[0];
    if (!m) return null;
    const cam = {
      id: uid('c'),
      name: opts.name || 'CAM-' + String(d.cams.length + 1).padStart(2, '0'),
      modelId: m.id,
      x, y,
      h: opts.h ?? (m.mount === 'ceil' ? 3.0 : 2.8),
      pan: opts.pan ?? 0,
      tilt: opts.tilt ?? (m.pano >= 360 ? 90 : 15),
      focal: opts.focal ?? m.focal, profileId: opts.profileId || '', corridor: !!opts.corridor
    };
    cam.mountKind = opts.mountKind || placementMountKind(this, m);
    if (isCeilingKind(cam.mountKind)) {
      const pose = ceilingPlacement(this, { ...cam, mountDrop: opts.mountDrop }, m);
      if (!pose) return null;
      Object.assign(cam, pose);
    } else if (isPoleKind(cam.mountKind)) {
      const pose = poleCameraPose(d, { ...cam, mountPoleId: opts.mountPoleId, mountAzimuth: opts.mountAzimuth }, m);
      if (!pose) return null;
      Object.assign(cam, pose);
    }
    this.snapshot();
    this.useModel(m.id);
    d.cams.push(cam);
    this.sel = { type: 'cam', id: cam.id };
    this.touch();
    return cam;
  }

  cancelRoomAreaRemoval(requestID) {
    if (!this.roomAreaRemovalRequest || requestID !== undefined && this.roomAreaRemovalRequest.id !== requestID) return false;
    this.roomAreaRemovalRequest = null; this.emit(); return true;
  }

  confirmRoomAreaRemoval(requestID) {
    const request = this.roomAreaRemovalRequest;
    if (!request || request.id !== requestID || !request.review) throw new Error('This room removal is no longer ready. Review the room again.');
    return applyRoomAreaRemovalReview(this, request.review);
  }

  remove(sel = this.sel) {
    if (sel?.type === 'area') {
      // Resolve a shared-ground rendering selection before looking up a local
      // ID; different buildings may legitimately reuse that ID.
      let matches = [], resolutionError = '';
      try {
        if (this._edit) throw new Error('Finish the current edit before reviewing room removal.');
        if (!Array.isArray(this.doc.areas) || this.doc.areas.length > 200 || this.doc.areas.some(a => !a || typeof a !== 'object') || !Array.isArray(this.doc.floors) || this.doc.floors.length > 127)
          throw new Error('The room or floor collection needs review before removing an area.');
        let resolved = sel;
        if (sel.floorId === undefined && !this.doc.areas.some(a => a.id === sel.id)) resolved = groundSelection(this.groundViewDoc, sel);
        const floorId = resolved.floorId ?? this.doc.activeFloorId;
        let owner = buildingID(this.doc);
        if (floorId !== this.doc.activeFloorId) {
          const targets = this.doc.floors.filter(f => f?.id === floorId);
          if (targets.length !== 1 || !targets[0].plan || !isGroundLevel(this.doc) || !isGroundLevel(targets[0])) throw new Error('Choose the room on its owning floor before removing it.');
          owner = targets[0].buildingId ?? buildingID(targets[0].plan);
          if (targets[0].buildingId !== undefined && targets[0].plan.buildingId !== undefined && targets[0].buildingId !== targets[0].plan.buildingId) throw new Error('The selected room has ambiguous building ownership.');
        }
        if (sel.buildingId !== undefined && sel.buildingId !== owner) throw new Error('The selected room building changed. Review its owner before removing it.');
        // A mismatched composite selection must not even switch the view.
        if (floorId !== this.doc.activeFloorId) {
          // Validate raw ownership before activateSiteLevel normalizes the
          // target snapshot into the active floor. It must not repair invalid
          // saved ownership/elevation merely by changing the view.
          validateWallEditSite(this.doc);
          this.switchFloor(floorId, { viewOnly: true });
        }
        sel = { type: 'area', id: resolved.id };
        if (!Array.isArray(this.doc.areas) || this.doc.areas.some(a => !a || typeof a !== 'object')) throw new Error('The room collection needs review before removing an area.');
        matches = this.doc.areas.filter(a => a.id === sel.id);
      } catch (failure) { resolutionError = failure.message; }
      const directAreaRemoval = matches.length === 1 && (matches[0].kind === 'exclude' || matches[0].kind === 'floor' && matches[0].environment === 'outdoor');
      if (resolutionError || !directAreaRemoval) {
        const selection = Object.freeze({ type: 'area', id: sel.id, floorId: this.doc.activeFloorId, buildingId: buildingID(this.doc) });
        let review = null, error = '';
        try {
          if (resolutionError) throw new Error(resolutionError);
          if (this._edit) throw new Error('Finish the current edit before reviewing room removal.');
          review = reviewRoomAreaRemoval(this.doc, selection);
        } catch (failure) { error = failure.message; }
        // The identity and selected branded review cannot be swapped after a
        // modal has rendered. The engine separately guards candidate tampering.
        this.roomAreaRemovalRequest = Object.freeze({ id: uid('room-removal'), selection, review, error });
        this.emit(); return this.roomAreaRemovalRequest;
      }
    }
    if (!sel || (sel.type === 'cam' && this.doc.cams.find(c => c.id === sel.id)?.locked)) return;
    const d = this.doc;
    if(sel.type==='wall'&&(d.wallLengthLocks||[]).some(l=>l.state==='active'&&l.wallId===sel.id))return this.reportEditError('This wall has a locked length. Use Field tools → Wall length locks to unlock it before deleting it.');
    this.snapshot();
    if (sel.type === 'cam') { d.cams = d.cams.filter(c => c.id !== sel.id); d.cables = d.cables.filter(c => c.camId !== sel.id); d.accessories = d.accessories.filter(a => a.camId !== sel.id); }
    if (sel.type === 'wall') d.walls = d.walls.filter(w => w.id !== sel.id);
    if (sel.type === 'stair') d.stairs = d.stairs.filter(s => s.id !== sel.id);
    for (const [type, key] of [['light', 'lights'], ['cable', 'cables'], ['target', 'targets'], ['task', 'tasks'], ['obstacle', 'obstacles'], ['opening', 'openings']]) if (sel.type === type) d[key] = d[key].filter(t => t.id !== sel.id);
    if (sel.type === 'area') d.areas = d.areas.filter(a => a.id !== sel.id);
    if (sel.type === 'zone') d.zones = d.zones.filter(z => z.id !== sel.id);
    if (sel.type === 'equipment') { d.equipment = d.equipment.filter(e => e.id !== sel.id); d.cables = d.cables.filter(c => c.equipmentId !== sel.id); }
    if (sel.type === 'bg') d.bg = null;
    this.sel = null;
    this.touch();
  }

  duplicateCam() {
    const c = this.selected();
    if (!c || this.sel?.type !== 'cam') return;
    const d = this.doc;
    const n = { ...structuredClone(c), locked: false, id: uid('c'), name: 'CAM-' + String(d.cams.length + 1).padStart(2, '0'), x: c.x + 1, y: c.y + 1 };
    if (isCeilingCamera(d, n)) {
      const pose = ceilingCameraPose(d, n) || ceilingCameraPose(d, { ...n, x: c.x, y: c.y });
      if (!pose) return;
      Object.assign(n, pose);
    } else if (isPoleCamera(d, n)) {
      const pose = poleCameraPose(d, { ...n, mountAzimuth: (c.mountAzimuth || 0) + 180, pan: c.pan + 180 });
      if (!pose) return;
      Object.assign(n, pose);
    } else if (this.snapCameras) {
      const mount = snapWallCamera(d, n.x, n.y, n.h, 1.25, n);
      n.x = mount?.x ?? c.x; n.y = mount?.y ?? c.y; n.pan = mount?.pan ?? c.pan;
    }
    this.snapshot();
    d.cams.push(n);
    this.sel = { type: 'cam', id: n.id };
    this.touch();
  }

  setCameraMount(camera, kind, drop = camera.mountDrop) {
    if (camera.locked) return 'Unlock the camera before changing its mount.';
    const model = this.model(camera.modelId);
    if (!mountingProfile(model).kinds.includes(kind)) return 'This mounting position is not supported by the model profile.';
    let pose = { ...camera, mountKind: kind, mountDrop: drop, mountSurface: '', mountPoleId: '' };
    if (isCeilingKind(kind)) {
      pose = ceilingCameraPose(this.doc, pose, model);
      if (!pose) return 'No clear ceiling here. Add a roof / ceiling to the building, or place beneath an upper floor slab.';
    } else if (isPoleKind(kind)) {
      pose = camera.mountPoleId ? poleCameraPose(this.doc, { ...pose, mountPoleId: camera.mountPoleId }, model) : snapPoleCamera(this.doc, camera, model, pose, 5);
      if (!pose) return `Place an exterior pole within ${formatLength(5, this.doc)} first. Use outdoor-rated hardware and a lens height below the pole top.`;
    } else if (kind !== cameraMountKind(model, camera)) {
      const fromOtherSupport = isCeilingKind(cameraMountKind(model, camera)) || isPoleCamera(this.doc, camera);
      const wall = snapWallCamera(this.doc, camera.x, camera.y, fromOtherSupport ? Math.min(camera.h, 2.8) : camera.h, 100000, pose);
      if (!wall) return 'No allowed wall can support this camera. Add a solid wall first.';
      Object.assign(pose, { x: wall.x, y: wall.y, h: wall.h, pan: wall.pan });
      if (!fromOtherSupport && validWallPose(this.doc, { ...pose, pan: camera.pan }, wall.wall)) pose.pan = camera.pan;
    }
    const changedKind = kind !== cameraMountKind(model, camera);
    this.snapshot(); Object.assign(camera, pose); if (changedKind) camera.mountPart = ''; this.touch(); return '';
  }

  setCameraHeight(camera, height) {
    if (camera.locked) return 'Unlock the camera before changing its height.';
    if (!Number.isFinite(height) || height < 0.2 || height > 60) return `Enter a lens height between ${formatLength(.2, this.doc)} and ${formatLength(60, this.doc)}.`;
    let pose = { ...camera, h: height };
    if (isCeilingCamera(this.doc, camera)) {
      if (camera.mountKind !== 'ceiling-pendant') return 'Choose a pendant mount to lower the lens beneath the ceiling.';
      pose = ceilingCameraPose(this.doc, camera, undefined, { keepSurface: true, lensHeight: height });
    } else if (isPoleCamera(this.doc, camera)) pose = poleCameraPose(this.doc, pose);
    if (!pose) return 'This height does not fit the support or the bracket is obstructed.';
    this.snapshot(); Object.assign(camera, pose); this.touch(); return '';
  }

  setPoleAttachment(camera, mountPoleId, mountAzimuth = camera.mountAzimuth || 0) {
    if (camera.locked) return 'Unlock the camera before changing its mount.';
    const delta = mountAzimuth - (camera.mountAzimuth || 0);
    const pose = poleCameraPose(this.doc, { ...camera, mountPoleId, mountAzimuth, pan: camera.pan + delta });
    if (!pose) return 'This pole cannot support the camera at this height and bearing. Check outdoor suitability and obstructions.';
    this.snapshot(); Object.assign(camera, pose); this.touch(); return '';
  }

  setOpt(patch) {
    this.snapshot();
    Object.assign(this.doc.opt, patch);
    this.touch();
  }
  setMeasurementSystem(measurementSystem) {
    const value = measurementSystem === 'imperial' ? 'imperial' : 'metric';
    if ((this.doc.opt.measurementSystem || 'metric') === value && this.doc.floors.every(f => (f.plan?.opt?.measurementSystem || 'metric') === value)) return;
    this.snapshot();
    this.doc.opt.measurementSystem = value;
    for (const floor of this.doc.floors) {
      floor.plan.opt ||= {};
      floor.plan.opt.measurementSystem = value;
    }
    if (this.cov) this.cov.findings = audit(this.doc, this.cov);
    this.touch({ reevaluate: false });
  }
  setThreshold(patch) {
    this.snapshot();
    Object.assign(this.doc.opt.t, patch);
    this.doc.opt.profileName = 'Custom project thresholds';
    // Retain the selected vocabulary when customizing its numeric values.
    this.touch();
  }

  setProfile(id) { this.snapshot(); chooseProfile(this.doc, id); this.touch(); }

  addFloor(name = 'New floor', { elevation = (this.doc.elevation || 0) + 3, copy = 'empty' } = {}) {
    if (buildingFloors(this.doc).length >= 32 || this.doc.floors.length >= 127) throw new Error('The floor limit has been reached.');
    this.snapshot();
    const id = uid('floor');
    const next = { ...blankDoc(), opt: structuredClone(this.doc.opt), floorName: name, activeFloorId: id, elevation, buildingId: buildingID(this.doc) };
    if (copy !== 'empty') next.areas = this.doc.areas.filter(a => a.kind === 'floor' && a.environment !== 'outdoor').map(a => ({ ...structuredClone(a), id: uid('a') }));
    if (copy === 'structure') {
      const ids = new Map();
      next.walls = this.doc.walls.map(w => { const id = uid('w'); ids.set(w.id, id); return { ...w, id }; });
      next.walls = copyWallSplitLineages(next.walls, ids);
      next.openings = this.doc.openings.filter(o => ids.has(o.wallId)).map(o => ({ ...o, id: uid('opening'), wallId: ids.get(o.wallId), inferredId: '' }));
      next.wallLengthLocks = copyWallLengthLocks(this.doc, next, ids).wallLengthLocks;
    }
    const current = registerBuildings(structuredClone(this.toDocument()));
    this.doc = { ...next, stairs: current.stairs, lighting: current.lighting, ...(current.buildings ? { buildings: current.buildings } : {}), floors: [...current.floors, floorSnapshot(current)] };
    this.sel = null; this.probe = null; this.bgImage = null; this.touch();
  }

  switchFloor(id, { viewOnly = false } = {}) {
    if (id === SITE_GROUND) id = (groundFloor(this.doc) || groundLevels(this.doc)[0])?.id;
    const target = this.doc.floors.find(f => f.id === id);
    if (!target) return;
    this.roomAreaRemovalRequest = null;
    if (!viewOnly) this.snapshot();
    this.doc = activateSiteLevel(this.toDocument(), id);
    this.sel = null; this.probe = null; this.bgImage = null; this.touch({ dirty: !viewOnly });
  }

  switchBuilding(id, options = {}) {
    const floors = buildingFloors(this.doc, id);
    const remembered = this._buildingFloors?.[id];
    if (!floors.length) return;
    this._buildingFloors ||= {};
    this._buildingFloors[buildingID(this.doc)] = this.doc.activeFloorId;
    const ground = groundFloor(this.doc, id);
    const target = isGroundLevel(this.doc) && ground ? ground : floors.find(f => f.id === remembered) || ground || floors[0];
    this.switchFloor(target.id, options);
  }

  addBuilding(name = 'New building') {
    if (siteBuildings(this.doc).length >= 32 || this.doc.floors.length >= 127) throw new Error('The building limit has been reached.');
    this.snapshot();
    const current = registerBuildings(structuredClone(this.toDocument())), id = uid('building');
    this.doc = { ...blankDoc(), opt: structuredClone(current.opt), activeFloorId: uid('floor'), floorName: 'Ground floor', elevation: 0, buildingId: id,
      buildings: [...current.buildings, { id, name: name.trim() || 'New building' }], lighting: current.lighting, stairs: current.stairs,
      floors: [...current.floors, floorSnapshot(current)] };
    this.sel = null; this.probe = null; this.bgImage = null; this.touch();
  }

  renameBuilding(name) {
    this.snapshot(); registerBuildings(this.doc);
    this.doc.buildings.find(b => b.id === buildingID(this.doc)).name = name.trim() || 'Building';
    this.touch({ reevaluate: false });
  }

  separateBuilding(areaID, name) {
    const result = separateBuilding(this.toDocument(), areaID, name);
    this.snapshot(); this.doc = result.doc;
    this.sel = null; this.probe = null; this.bgImage = null; this.touch();
    return result.summary;
  }

  addStair(point) {
    const value = defaultStair(this.doc, point);
    if (!value || this.doc.stairs.length >= 100) return null;
    this.snapshot();
    const stair = { id: uid('stair'), ...value }; this.doc.stairs.push(stair);
    this.sel = { type: 'stair', id: stair.id }; this.setTool('select'); this.touch();
    return stair;
  }

  /** Bring the library's current specs into this plan's snapshot. Models the
   *  plan already uses are updated in place; nothing in use is ever dropped. */
  syncModels(catalogue) {
    const floors = [this.doc, ...(this.doc.floors || []).map(f => f.plan)];
    const used = new Set(floors.flatMap(f => f.cams.map(c => c.modelId)));
    const lockedModels = new Set(floors.flatMap(f => f.cams.filter(c => c.locked).map(c => c.modelId)));
    const byId = new Map(catalogue.filter(m => !lockedModels.has(m.id)).map(m => [m.id, m]));
    this.snapshot();
    this.rememberModels(catalogue);
    for (const floor of floors) {
    floor.models = floor.models.map(m => {
      if (!used.has(m.id) || !byId.has(m.id)) return m;
      const quote = floor.planning?.quotes?.find(q => q.modelId === m.id);
      return { ...byId.get(m.id), ...(quote ? { cost: quote.cost, currency: quote.currency, supplier: quote.supplier || '', priceDate: quote.priceDate || '' } : {}) };
    });
    for (const c of floor.cams) {
      if (!byId.has(c.modelId)) continue;
      const m = floor.models.find(m => m.id === c.modelId);
      if (m && !c.locked) { c.focal = clamp(c.focal, m.fmin, m.fmax); if (m.pano >= 360) c.tilt = 90; }
    }
    }
    this.touch();
  }
}

/** Subscribe a component to the store. Returns the store for convenience. */
export function usePlan(store) {
  useSyncExternalStore(store.subscribe, store.getRev, store.getRev);
  return store;
}

export { clamp };
